// Central configuration. Everything env-driven so Andrew can point the app at the
// real R2 feeds without touching code. Sensible defaults keep dev runnable.
//
// PLATFORM NOTE: this file reads `import.meta.env` (Vite). It's the one build-env
// boundary the RN port adapts (Metro env / expo-constants) — the exported `config`
// object shape stays identical, so lib/* consumers don't change.

const base = (import.meta.env.VITE_R2_BASE_URL ?? '').replace(/\/$/, '')

function feed(path: string | undefined, fallback: string): string {
  const p = path ?? fallback
  // Allow absolute overrides, otherwise resolve against the R2 base.
  return /^https?:\/\//.test(p) ? p : `${base}${p}`
}

export const config = {
  /**
   * NWS API — free, US-only. Called DIRECTLY from the browser: api.weather.gov
   * sends `access-control-allow-origin: *` on GET and OPTIONS (verified), and NWS
   * explicitly prefers direct clients over shared proxy IPs (proxies hit rate
   * limits far sooner). Browsers control the User-Agent header (it's forbidden to
   * set on fetch), and NWS accepts the browser's UA. The service worker layers on
   * NetworkFirst caching, so a proxy buys us nothing here.
   */
  nws: {
    baseUrl: 'https://api.weather.gov',
  },

  /** Web push */
  push: {
    vapidPublicKey: import.meta.env.VITE_VAPID_PUBLIC_KEY ?? '',
    apiBase: (import.meta.env.VITE_PUSH_API_BASE ?? '/api').replace(/\/$/, ''),
  },

  /**
   * Standalone weather-MAP (Mapbox GL). The Mapbox token is a PUBLIC token supplied
   * as a build env var (Andrew sets it in Pages) — never hardcoded, never a secret.
   * `renderApi` points at the services/radar-render on-demand endpoint (/render).
   */
  map: {
    mapboxToken: import.meta.env.VITE_MAPBOX_TOKEN ?? '',
    renderApi: (import.meta.env.VITE_RADAR_RENDER_API ?? '').replace(/\/$/, ''),
    /** Dark base style for M1 (the style picker lands in M2). */
    style: 'mapbox://styles/mapbox/dark-v11',
  },

  /** Content layer — Triple-A's existing R2 products & JSON feeds (read-only). */
  r2: {
    base,
    feeds: {
      cyclolab: feed(import.meta.env.VITE_FEED_CYCLOLAB, '/cyclolab/latest.json'),
      floaters: feed(import.meta.env.VITE_FEED_FLOATERS, '/floaters/index.json'),
      meso: feed(import.meta.env.VITE_FEED_MESO, '/meso/index.json'),
      models: feed(import.meta.env.VITE_FEED_MODELS, '/models/index.json'),
      radar: feed(import.meta.env.VITE_FEED_RADAR, '/radar/index.json'),
      tropics: feed(import.meta.env.VITE_FEED_TROPICS, '/tropics/global.json'),
      spc: feed(import.meta.env.VITE_FEED_SPC, '/spc/outlook_today.json'),
    },
  },
} as const

/** True only when Andrew has actually pointed VITE_R2_BASE_URL at his bucket. */
export const hasR2 = Boolean(base)
/** True once a VAPID public key is configured. */
export const hasPush = Boolean(config.push.vapidPublicKey)
/** True once the PUBLIC Mapbox token is configured (the weather-map needs it). */
export const hasMapbox = Boolean(config.map.mapboxToken)
